import React, { useState } from "react";
import { useAgentApi } from "../../services/AgentApi";
import { useAppContext } from "../../contexts/AppContext";
import Dialog from "carbon-react/lib/components/dialog";
import { Row, Column } from "carbon-react/lib/components/row";
import {
  Select,
  Option,
} from "carbon-react/lib/__experimental__/components/select";
import Heading from "carbon-react/lib/components/heading";
import Content from "carbon-react/lib/components/content";
import TextArea from "carbon-react/lib/__experimental__/components/textarea";
import {
  getAllAgentRoles,
  getAgentRoleDisplayValue,
} from "../../constants/model";
import ErrorMessage from "../messages/ErrorMessage";
import DialogConfirmButtons from "../common/dialogConfirmButtons/DialogConfirmButtons";

const AgentChangeRoleDialog = ({ open, onClose, agent, onAgentUpdated }) => {
  const [saving, setSaving] = useState(false);
  const [newRole, setNewRole] = useState(null);
  const [agentComments, setAgentComments] = useState("");
  const [validAgentComments, setValidAgentComments] = useState(true);
  const [validNewRole, setValidNewRole] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const { agentEntitlement, toastSuccess } = useAppContext();
  const { assignRole } = useAgentApi();

  const onDialogClose = () => {
    setNewRole(null);
    setAgentComments("");
    setValidAgentComments(true);
    setValidNewRole(true);
    setShowError(false);
    onClose();
  };

  const onDialogCancel = () => {
    if (!saving) onDialogClose();
  };

  const validateAgentComments = () => {
    let valid = true;

    if (agentComments?.length === 0) {
      valid = false;
    }

    setValidAgentComments(valid);
    return valid;
  };

  const validateNewRole = () => {
    let valid = true;

    if (!newRole || newRole?.length < 1) {
      valid = false;
    }

    setValidNewRole(valid);
    return valid;
  };

  const validateInputs = () => {
    let valid = true;
    if (!validateAgentComments()) valid = false;
    if (!validateNewRole()) valid = false;
    return valid;
  };

  const onDialogSave = async () => {
    if (saving) return;
    if (!validateInputs()) return;
    try {
      setErrorMsg("");
      setSaving(true);
      const result = await assignRole(agent.id, newRole, agentComments);
      if (!result.success)
        throw new Error(
          result.error?.description || "Failed to apply role change"
        );

      const updatedAgent = result.data;
      onAgentUpdated(updatedAgent);
      toastSuccess(
        `New role '${getAgentRoleDisplayValue(
          updatedAgent.role
        )}' assigned to agent '${agent.email}'.`
      );
      onDialogClose();
    } catch (error) {
      if (error) setErrorMsg(error.toString());
      setShowError(true);
    }
    setSaving(false);
  };

  const isAnAdministrableRole = (role) => {
    return (
      agentEntitlement &&
      agentEntitlement.agents.administrableRoles.includes(role.value)
    );
  };

  const getValidAgentRoles = () => {
    return agent
      ? getAllAgentRoles().filter(
          (role) => role.value !== agent.role && isAnAdministrableRole(role)
        )
      : null;
  };

  const onSelectedRoleChange = (role) => {
    setNewRole(role);
  };

  const roleSelectionRow = agent ? (
    <Row columns="5">
      <Column columnSpan="2" columnAlign="centre">
        <Select
          autoFocus={true}
          preventFocusAutoOpen={true}
          disabled={saving}
          filterable={false}
          value={newRole}
          onBlur={validateNewRole}
          onChange={(e) =>
            onSelectedRoleChange(e?.target?.value[0]?.optionValue)
          }
          {...(!validNewRole ? { hasWarning: true } : {})}
          {...(!validNewRole ? { inputIcon: "warning" } : {})}
          {...(!validNewRole
            ? {
                tooltipMessage: "Please select a role.",
              }
            : {})}
        >
          {getValidAgentRoles().map((role) => {
            return (
              <Option
                key={`option-${role.value}`}
                text={role.displayValue}
                value={role.value}
              />
            );
          })}
        </Select>
      </Column>
    </Row>
  ) : (
    {}
  );

  const buttonRow = (
    <DialogConfirmButtons
      appearBusy={saving}
      confirmBtnLabel="Change Role"
      onConfirm={onDialogSave}
      onCancel={onDialogCancel}
    />
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        // TODO: We need the error message from the response
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed!"
          }
        />
      )}
    </>
  );

  return (
    <>
      {agent ? (
        <Dialog open={open} showCloseIcon={true} onCancel={onDialogCancel}>
          <Heading title="Change Agent Role" />
          <Content
            title={`${agent.email} (${getAgentRoleDisplayValue(agent.role)})`}
          >
            Changing an agent's role will impact the features that this agent
            has access to whilst using the application
          </Content>
          <Content title="Confirm">
            Before proceeding, please ensure that you've selected the correct
            agent.
          </Content>
          <Content title="Select the desired new role for the agent">
            {roleSelectionRow}
          </Content>
          <Content title="Comments (Required)">
            <TextArea
              name="change-role-agent-comments"
              disabled={saving}
              value={agentComments}
              characterLimit={"250"}
              enforceCharacterLimit={true}
              placeholder={
                "Please indicate why this action is being performed."
              }
              onChange={(e) => {
                setValidAgentComments(true);
                setAgentComments(e.target.value);
              }}
              onBlur={validateAgentComments}
              {...(!validAgentComments ? { hasWarning: true } : {})}
              {...(!validAgentComments ? { inputIcon: "warning" } : {})}
              {...(!validAgentComments
                ? {
                    tooltipMessage:
                      "Please indicate why this action is being performed.",
                  }
                : {})}
            />
          </Content>
          {buttonRow}
          {errorMessage}
        </Dialog>
      ) : null}
    </>
  );
};

export default AgentChangeRoleDialog;
